import { useEffect, useMemo, useRef } from "react";
import { useMap } from "react-leaflet";
import {
  TAKE_LOCATION_COLLAPSE_EVENT,
  TAKE_LOCATION_COLLAPSE_MS,
} from "./takeLocationAnimation";
import { useDispatch, useSelector } from "react-redux";
import { TakePosition } from "../../app/slices/EnvironmentSlice";
import {
  selectExpandedTakeLocationIds,
  setExpandedTakeLocationIds,
  toggleTakeLocationExpanded,
} from "../../app/slices/GlobalSlice";
import {
  selectActors,
  selectSelectedElement,
  selectSelectedTake3D,
} from "../../app/slices/TrainingSlice";
import {
  getActorAvatarCssColor,
  getActorAvatarInitial,
  getActorAvatarSoftStyle,
} from "../../helpers/actorAvatar";
import { Actor } from "../../models/Actor";
import InteractionPointPreview, {
  CollapsedLocationPointStrategy,
  TakeLocationPinPointStrategy,
} from "./InteractionPointPreview";
import { Viewport3DLayerProps } from "./Viewport3DEnvironmentMetadata";
import {
  countTakeLocationPositions,
  getActorsPlacedAtTakeLocation,
} from "./takeLocationCollapse";

const MAX_AVATARS = 3;
/** Keeps the pin above the position markers it toggles, whatever their latitude. */
export const TAKE_LOCATION_PIN_Z_INDEX_OFFSET = 1000;
/** Location pins that stay usable during Set Camera picking (see InteractionPointPreview.css). */
export const LOCATION_PIN_PICKABLE_CLASS = "interactionpointpreview-location-pin-pickable";

/**
 * One take-location pin per location other than the selected take's own.
 * Every location starts folded: its NPC, prop and situation positions are
 * hidden behind the pin, which shows a position count and the actors placed
 * there. Clicking the pin unfolds the location; clicking again folds it.
 * Purely a focus tool — it never changes the take.
 */
export default function Viewport3DTakeLocationCollapseLayer(
  props: Viewport3DLayerProps
) {
  const { currentTakeLocation, takePositions, isCameraSelected } = { ...props };
  const selectedElement = useSelector(selectSelectedElement);
  const dispatch = useDispatch();
  const map = useMap();
  const pendingCollapses = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const take = useSelector(selectSelectedTake3D);
  const actors = useSelector(selectActors) ?? [];
  const expandedFromStore = useSelector(selectExpandedTakeLocationIds);
  const expandedTakeLocationIds = useMemo(
    () => expandedFromStore ?? [],
    [expandedFromStore]
  );

  // A new take, or a take moved to another location, starts with every other
  // location folded again.
  const takeId = take?.id;
  const takeLocationId = take?.interactionPointId;
  useEffect(() => {
    const pending = pendingCollapses.current;
    return () => {
      pending.forEach(clearTimeout);
      pending.clear();
    };
  }, [takeId, takeLocationId]);
  useEffect(() => {
    dispatch(setExpandedTakeLocationIds([]));
  }, [takeId, takeLocationId, dispatch]);

  if (!currentTakeLocation || !take?.interactionPointId) return <></>;

  // The take's own camera only uses its location, so pins stay locked while it is
  // picked. A Set Camera can cut to any location: there, pins unfold to show cameras.
  const isSetCameraPicking =
    isCameraSelected && !!selectedElement && selectedElement.id !== take.mainCamera?.id;
  const pinsLocked = isCameraSelected && !isSetCameraPicking;

  const renderAvatars = (placedActors: Actor[]) => {
    const shown = placedActors.slice(0, MAX_AVATARS);
    const overflow = placedActors.length - shown.length;

    return (
      <>
        {shown.map((actor) => (
          <div
            key={actor.id}
            className="interactionpointpreview-node-avatar"
            style={getActorAvatarSoftStyle(getActorAvatarCssColor(actor))}
          >
            {actor.id === "player" ? "P" : getActorAvatarInitial(actor.workingName)}
          </div>
        ))}
        {overflow > 0 && (
          <div
            className="interactionpointpreview-node-avatar"
            style={getActorAvatarSoftStyle("#919BA7")}
          >
            +{overflow}
          </div>
        )}
      </>
    );
  };

  const renderTooltip = (
    point: TakePosition,
    collapsed: boolean,
    count: number,
    placedActors: Actor[]
  ) => {
    if (!collapsed) {
      return <span className="font-medium">Collapse {point.name}</span>;
    }

    const actorNames = placedActors.map((actor) => actor.workingName);
    const details = [
      `${count} position${count === 1 ? "" : "s"}`,
      placedActors.length > 0
        ? `${placedActors.length} actor${
            placedActors.length === 1 ? "" : "s"
          } placed (${actorNames.join(", ")})`
        : undefined,
      "click to expand",
    ].filter(Boolean);

    return (
      <div className="flex flex-col">
        <span className="font-medium">{point.name}</span>
        <span className="text-[9.5px] opacity-80">{details.join(" · ")}</span>
      </div>
    );
  };

  const pins = takePositions
    .filter((point) => point.id !== currentTakeLocation.id)
    .map((point) => {
      const collapsed = !expandedTakeLocationIds.includes(point.id);
      const count = countTakeLocationPositions(point);
      const placedActors = collapsed
        ? getActorsPlacedAtTakeLocation(point, take, actors)
        : [];

      return (
        <InteractionPointPreview
          key={point.id}
          strategy={
            collapsed
              ? CollapsedLocationPointStrategy
              : TakeLocationPinPointStrategy
          }
          x={point.position.x}
          y={point.position.y}
          markerBorderColor={point.take_color}
          markerIconColor={point.take_color}
          zIndexOffset={TAKE_LOCATION_PIN_Z_INDEX_OFFSET}
          disabled={pinsLocked}
          iconClassName={isSetCameraPicking ? LOCATION_PIN_PICKABLE_CLASS : undefined}
          onClick={() => {
            if (pinsLocked || pendingCollapses.current.has(point.id)) return;
            if (!collapsed && !window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) {
              map.fire(TAKE_LOCATION_COLLAPSE_EVENT, { locationId: point.id });
              pendingCollapses.current.set(point.id, setTimeout(() => {
                pendingCollapses.current.delete(point.id);
                dispatch(toggleTakeLocationExpanded(point.id));
              }, TAKE_LOCATION_COLLAPSE_MS));
              return;
            }
            dispatch(toggleTakeLocationExpanded(point.id));
          }}
          badgeContent={collapsed ? count : undefined}
          badgeClassName="interactionpointpreview-node-badge--take"
          badgeColor={point.take_color}
          footerContent={
            collapsed && placedActors.length > 0
              ? renderAvatars(placedActors)
              : undefined
          }
          tooltipContent={renderTooltip(point, collapsed, count, placedActors)}
        />
      );
    });

  return <>{pins}</>;
}
